import React, { useEffect, useRef } from "react";
import {
  MAPLABELS,
  MAPLABEL_MAP_CONNECTOR_END_TUCK,
  MAPLABEL_MAP_ICON_OFFSET_Y,
  MAPLABEL_MAP_TEXT_OFFSET_Y,
  MAPLABEL_MAP_TEXT_SIZE,
} from "../../constants/maplabels";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { preloadMapSymbolImages } from "../../utils/mapSymbolImages";

function createGeoJSONCircle(center, radiusInMeters, points = 64) {
  const { latitude: latCenter, longitude: lonCenter } = {
    latitude: center[1],
    longitude: center[0],
  };
  const coords = [];
  const R = 6378137;
  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const lat = Math.asin(
      Math.sin((latCenter * Math.PI) / 180) * Math.cos(radiusInMeters / R) +
        Math.cos((latCenter * Math.PI) / 180) *
          Math.sin(radiusInMeters / R) *
          Math.cos(theta)
    );
    const lon =
      (lonCenter * Math.PI) / 180 +
      Math.atan2(
        Math.sin(theta) *
          Math.sin(radiusInMeters / R) *
          Math.cos((latCenter * Math.PI) / 180),
        Math.cos(radiusInMeters / R) -
          Math.sin((latCenter * Math.PI) / 180) * Math.sin(lat)
      );
    coords.push([(lon * 180) / Math.PI, (lat * 180) / Math.PI]);
  }
  coords.push(coords[0]);
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [coords] },
  };
}

const CONNECTOR_VISIBLE_ZOOM = 13;

const ConfirmAttendanceMap = ({
  event,
  allowedRadius = 100,
  userCoords,
  focusUserLocation = false,
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const sourceIdsRef = useRef({
    event: `single-event-${Math.random().toString(36).slice(2, 10)}`,
    radius: `radius-${Math.random().toString(36).slice(2, 10)}`,
    connector: `connector-${Math.random().toString(36).slice(2, 10)}`,
    userLocation: `user-location-${Math.random().toString(36).slice(2, 10)}`,
  });

  useEffect(() => {
    if (!event) return;
    setMapLoaded(false);
    const sourceIds = sourceIdsRef.current;
    const map = new maplibregl.Map({
      container: mapRef.current,
      style:
        "https://api.maptiler.com/maps/0196259d-93a4-75bb-af54-6b003b7377ab/style.json?key=QMa6CZX7KdzCIRISMOjh",
      center: [event.longitude, event.latitude],
      zoom: 15,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    mapInstance.current = map;

    map.once("style.load", async () => {
      if (!map || typeof map.getSource !== "function") return;
      await preloadMapSymbolImages(map, MAPLABELS);

      if (!map.getSource(sourceIds.event)) {
        map.addSource(sourceIds.event, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [event.longitude, event.latitude],
                },
                properties: {
                  id: event.id,
                  name: event.name,
                  dotSize: event.dotSize ?? 8,
                  dotColor: event.dotColor ?? "#0073FF",
                  icon: event.icon ?? "bubble-blue",
                },
              },
            ],
          },
        });
      }

      const circleFeature = createGeoJSONCircle(
        [event.longitude, event.latitude],
        allowedRadius,
        64
      );
      if (!map.getSource(sourceIds.radius)) {
        map.addSource(sourceIds.radius, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [circleFeature],
          },
        });
      }
      if (!map.getLayer("radius-circle")) {
        map.addLayer({
          id: "radius-circle",
          type: "fill",
          source: sourceIds.radius,
          paint: { "fill-color": "#ffffff", "fill-opacity": 0.2 },
        });
      }
      if (!map.getLayer("radius-outline")) {
        map.addLayer({
          id: "radius-outline",
          type: "line",
          source: sourceIds.radius,
          paint: { "line-color": "#ffffff", "line-width": 1.5 },
        });
      }

      if (!map.getLayer("event-dot")) {
        map.addLayer({
          id: "event-dot",
          type: "circle",
          source: sourceIds.event,
          paint: {
            "circle-radius": ["get", "dotSize"],
            "circle-color": ["get", "dotColor"],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          },
        });
      }

      if (!map.getSource(sourceIds.connector)) {
        map.addSource(sourceIds.connector, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }
      if (!map.getLayer("event-connector")) {
        map.addLayer({
          id: "event-connector",
          type: "line",
          source: sourceIds.connector,
          paint: { "line-color": "#ffffff", "line-width": 2 },
        });
      }

      // Keep the same layer contract as the main events map: the connector is
      // above the dot but below the marker artwork, so it tucks under the
      // capsule instead of visibly piercing its lower border.
      if (!map.getLayer("event-bubble")) {
        map.addLayer({
          id: "event-bubble",
          type: "symbol",
          source: sourceIds.event,
          layout: {
            "icon-image": ["get", "icon"],
            "icon-size": 1,
            "icon-allow-overlap": false,
            "icon-anchor": "bottom",
            "icon-offset": [0, MAPLABEL_MAP_ICON_OFFSET_Y],
            "text-field": ["upcase", ["get", "name"]],
            "text-max-width": 25,
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": MAPLABEL_MAP_TEXT_SIZE,
            "text-anchor": "center",
            "text-justify": "center",
            "text-offset": [0, MAPLABEL_MAP_TEXT_OFFSET_Y],
            "text-padding": 2,
            "text-allow-overlap": false,
          },
          paint: {
            "text-color": "#ffffff",
            "text-opacity": ["step", ["zoom"], 0, 13, 1],
            "icon-opacity": ["step", ["zoom"], 0, 13, 1],
            "icon-color": ["get", "dotColor"],
          },
        });
      }

      const updateConnector = () => {
        if (!map || typeof map.getSource !== "function") return;
        const zoom = map.getZoom();
        const connectorsVisible = zoom >= CONNECTOR_VISIBLE_ZOOM;
        map.setLayoutProperty(
          "event-connector",
          "visibility",
          connectorsVisible ? "visible" : "none"
        );
        if (!connectorsVisible) return;
        const bubble = map.queryRenderedFeatures({
          layers: ["event-bubble"],
        })[0];
        if (!bubble) return;
        const [lng, lat] = bubble.geometry.coordinates;
        const px = map.project([lng, lat]);
        const dotRadius = Number(bubble.properties?.dotSize ?? event.dotSize ?? 8);
        const dotStrokeWidth = 2;
        const start = map.unproject({
          x: px.x,
          y: px.y - dotRadius - dotStrokeWidth / 2,
        });
        const end = map.unproject({
          x: px.x,
          y:
            px.y +
            MAPLABEL_MAP_ICON_OFFSET_Y -
            MAPLABEL_MAP_CONNECTOR_END_TUCK,
        });
        const connectorSource = map.getSource(sourceIds.connector);
        if (!connectorSource || typeof connectorSource.setData !== "function") return;
        connectorSource.setData({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: [
                  [start.lng, start.lat],
                  [end.lng, end.lat],
                ],
              },
            },
          ],
        });
      };
      ["move", "zoom", "render"].forEach((ev) =>
        map.on(ev, updateConnector)
      );
      updateConnector();

      setMapLoaded(true);
    });

    return () => {
      setMapLoaded(false);
      mapInstance.current = null;
      if (typeof map.remove === "function") {
        map.remove();
      }
    };
  }, [event, allowedRadius]);

  useEffect(() => {
    if (!userCoords || !mapLoaded) return;
    const map = mapInstance.current;
    if (!map || typeof map.getSource !== "function") return;
    const sourceIds = sourceIdsRef.current;
    if (!map.getSource(sourceIds.userLocation)) {
      map.addSource(sourceIds.userLocation, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "user-location",
        type: "circle",
        source: sourceIds.userLocation,
        paint: {
          "circle-color": "#ffffff",
          "circle-radius": 8,
          "circle-stroke-color": "#0073FF",
          "circle-stroke-width": 1,
        },
      });
    }
    const userLocationSource = map.getSource(sourceIds.userLocation);
    if (!userLocationSource || typeof userLocationSource.setData !== "function") return;
    userLocationSource.setData({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [userCoords.longitude, userCoords.latitude],
          },
        },
      ],
    });
    if (focusUserLocation && typeof map.flyTo === "function") {
      map.flyTo({
        center: [userCoords.longitude, userCoords.latitude],
        zoom: 16,
        essential: true,
      });
    }
  }, [userCoords, mapLoaded, focusUserLocation]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        borderRadius: 12,
        overflow: "hidden",
        background: "transparent",
      }}
    >
      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

export default ConfirmAttendanceMap;
